import React, { useState } from "react";
import { ChevronRight, Search } from "lucide-react";
import { GameCard } from "./GameCard";
import AdBanner from "./AdBanner";

// Game data structure
const gameCategories = {
  "Algebra & Patterns": [
    {
      id: "function-machine",
      name: "The Function Machine",
      description: "Discover the hidden rule! Input numbers and analyze the output to crack the code.",
      difficulty: "Variable",
      players: "1 Player",
      duration: "5-15 min",
      tags: ["Algebra", "Patterns", "Logic"],
      mathConcept: "Algebraic Functions & Patterns",
      mathConceptDescription: "Introduces the concept of a mathematical function (f(x) = y). Players must identify the linear or non-linear relationship mapping inputs to outputs.",
      featured: false,
    },
  ],
  "Arithmetic Challenges": [
    {
      id: "arithmetic-sprint",
      name: "Arithmetic Sprint",
      description: "Race against the clock! Solve as many math problems as you can in 60 seconds.",
      difficulty: "Medium",
      players: "1 Player",
      duration: "1 min",
      tags: ["Math", "Speed", "Arcade"],
      mathConcept: "Mental Math & Speed",
      mathConceptDescription: "Focuses on 'automatizing' basic arithmetic facts. Rapid retrieval of math facts moves them from working memory to long-term memory, freeing up cognitive resources for more complex problem-solving.",
    },
    {
      id: "cross-sums",
      name: "Cross Sums",
      description: "A Kakuro-style logic puzzle. Fill the grid so rows and columns match the target sums.",
      difficulty: "Medium",
      players: "1 Player",
      duration: "Unlimited",
      tags: ["Math", "Logic", "Puzzle"],
      mathConcept: "Arithmetic & Logic",
      mathConceptDescription: "Combines arithmetic summation with deductive logic (similar to Sudoku). Players must analyze intersections to find unique partitions of numbers that satisfy multiple row and column constraints simultaneously.",
    },
    {
      id: "twenty-four",
      name: "Make 24",
      description: "Use four numbers and basic arithmetic to equal 24.",
      difficulty: "Hard",
      players: "1 Player",
      duration: "Unlimited",
      tags: ["Math", "Arithmetic", "Puzzle"],
      mathConcept: "Arithmetic Operations & Order",
      mathConceptDescription: "Strengthens mental math skills by requiring the flexible use of addition, subtraction, multiplication, and division. It introduces the importance of Order of Operations (PEMDAS) in constructing valid equations.",
      featured: true,
    },
    {
      id: "four-fours",
      name: "Four Four Puzzle",
      description: "Use exactly four 4's to form every integer from 0 to 50!",
      difficulty: "Hard",
      players: "1 Player",
      duration: "Unlimited",
      tags: ["Math", "Puzzle", "Arithmetic"],
      mathConcept: "Arithmetic Operations & Order",
      mathConceptDescription: "Strengthens understanding of order of operations and properties of numbers. Requires creative use of factorials, square roots, and basic operators to reach target values.",
    },
  ],
  "Geometry & Spatial Reasoning": [
    {
      id: "isometric-builder",
      name: "Isometric Builder",
      description: "Spatial visualization challenge. Rebuild the 3D structure from the target view.",
      difficulty: "Medium",
      players: "1 Player",
      duration: "5-15 min",
      tags: ["Geometry", "3D", "Spatial"],
      mathConcept: "3D Spatial Visualization",
      mathConceptDescription: "Transforms 2D orthographic projections into 3D mental models. This skill is crucial for engineering, architecture, and calculus (visualizing volumes of revolution).",
      featured: false,
    },
  ],
  "Graph Theory & Networks": [
    {
      id: "dots-and-boxes",
      name: "Dots and Boxes",
      description: "Connect dots to close boxes. A classic pencil-and-paper strategy game of territory capture.",
      difficulty: "Easy",
      players: "2 Players",
      duration: "5-10 min",
      tags: ["Strategy", "Logic", "Classic"],
      mathConcept: "Graph Theory & Topology",
      mathConceptDescription: "Relates to the Euler characteristic (Vertices - Edges + Faces). The game is about controlling the formation of 'faces' (boxes) on a planar graph.",
    },
    {
      id: "hex",
      name: "Hex",
      description: "A profound strategy game of connection. Connect your sides with a continuous chain of hexagons.",
      difficulty: "Hard",
      players: "2 Players",
      duration: "5-20 min",
      tags: ["Strategy", "Connection", "Deep"],
      mathConcept: "Graph Connectivity & Nash Eq.",
      mathConceptDescription: "Played on a hexagonal grid, this game demonstrates connectivity in graphs. The 'Hex Theorem' proves that a draw is impossible, a fact equivalent to the Brouwer Fixed Point Theorem.",
    },
    {
      id: "sim",
      name: "Sim",
      description: "Graph theory game. Connect points but avoid creating a triangle of your own color!",
      difficulty: "Hard",
      players: "2 Players",
      duration: "5-10 min",
      tags: ["Graph Theory", "Geometry", "Strategy"],
      mathConcept: "Ramsey Theory & Graphs",
      mathConceptDescription: "A direct application of Ramsey Theory (specifically R(3,3)=6), which states that in any group of 6 points connected by lines of 2 colors, there must be a monochromatic triangle. The game forces players to avoid this inevitable structure.",
    },
  ],
  "Logic & Deduction": [
    {
      id: "lights-out",
      name: "Lights Out",
      description: "Electronic puzzle game. Toggle lights to turn them all off!",
      difficulty: "Variable",
      players: "1 Player",
      duration: "2-10 min",
      tags: ["Logic", "Pattern", "Puzzle"],
      mathConcept: "Parity & Linear Algebra",
      mathConceptDescription: "Can be modeled as a system of linear equations over a finite field (GF(2)). Solving the puzzle is equivalent to solving Ax=b where matrix A represents the light switches.",
      featured: true,
    },
    {
      id: "mastermind",
      name: "Mastermind",
      description: "Code breaking challenge. Guess the secret color combination with logic and deduction.",
      difficulty: "Medium",
      players: "1 Player",
      duration: "5-15 min",
      tags: ["Logic", "Deduction", "Classic"],
      mathConcept: "Combinatorics & Optimization",
      mathConceptDescription: "A problem of information theory and combinatorics. The goal is to maximize the 'information gain' from each guess to reduce the set of possible codes as quickly as possible (Minimax strategy).",
    },
    {
      id: "minesweeper",
      name: "Minesweeper",
      description: "Classic logic game. Clear the board without detonating any hidden mines!",
      difficulty: "Medium",
      players: "1 Player",
      duration: "5-15 min",
      tags: ["Logic", "Puzzle", "Classic"],
      mathConcept: "Probability & Deduction",
      mathConceptDescription: "Combines deductive logic with probability. Players use known information to deduce safe squares, but must sometimes calculate the probabilities of mine locations when logic alone is insufficient.",
      featured: true,
    },
    {
      id: "nonogram",
      name: "Nonograms",
      description: "Picross style logic puzzle. Paint the grid cells according to the numbers to reveal a hidden picture.",
      difficulty: "Variable",
      players: "1 Player",
      duration: "5-20 min",
      tags: ["Logic", "Puzzle", "Art"],
      mathConcept: "Discrete Tomography",
      mathConceptDescription: "Based on the mathematical problem of reconstructing a 2D matrix from its 1D orthogonal projections (row and column sums). It explores unique reconstruction and constraint propagation.",
    },
    {
      id: "sudoku",
      name: "Sudoku",
      description: "Classic 9x9 logic puzzle. Fill the grid so that every row, column, and 3x3 box contains 1-9.",
      difficulty: "Medium",
      players: "1 Player",
      duration: "10-20 min",
      tags: ["Logic", "Puzzle", "Classic"],
      mathConcept: "Constraint Satisfaction",
      mathConceptDescription: "A 'Constraint Satisfaction Problem' (CSP). Solvers must find a state that satisfies a set of strict rules (row, column, box uniqueness) by propagating constraints and eliminating impossible options.",
      featured: true,
    },
    {
      id: "zip",
      name: "Zip",
      description: "Connect the numbers! Fill the grid with a continuous path of sequential numbers.",
      difficulty: "Variable",
      players: "1 Player",
      duration: "5-10 min",
      tags: ["Logic", "Puzzle", "Topology"],
      mathConcept: "Hamiltonian Path",
      mathConceptDescription: "A variation of the Hamiltonian Path problem. The goal is to visit every node (cell) in a graph exactly once. Finding such a path is generally NP-complete, but here you solve it through logical deduction.",
      featured: true,
    },
    {
      id: "spinning-table",
      name: "Spinning Table Puzzle",
      description: "A blind strategy puzzle. Align hidden coins on a rotating table.",
      difficulty: "Hard",
      players: "1-2 Players",
      duration: "5-15 min",
      tags: ["Logic", "Strategy", "Blind"],
      mathConcept: "Group Theory & Algorithms",
      mathConceptDescription: "A problem related to state machines and algorithmic solvability without full information. It demonstrates how to traverse a state space blindly to reach a target state (convergence).",
    },
  ],
  "Number Theory & Cryptography": [
    {
      id: "goldbachs-garden",
      name: "Goldbach's Garden",
      description: "A peaceful puzzle game. Pick matching prime flowers to sum up to the even target number.",
      difficulty: "Easy",
      players: "1 Player",
      duration: "Unlimited",
      tags: ["Math", "Puzzle", "Relaxing"],
      mathConcept: "Goldbach's Conjecture",
      mathConceptDescription: "Explores the famous unsolved conjecture stating that every even integer greater than 2 is the sum of two primes. Players build intuition for additive number theory.",
    },
    {
      id: "prime-factor-defense",
      name: "Prime Factor Defense",
      description: "Tower Defense strategy! Use Prime Towers (2, 3, 5, 7) to divide composite enemies down to 1.",
      difficulty: "Medium",
      players: "1 Player",
      duration: "10-30 min",
      tags: ["Strategy", "Math", "Tower Defense"],
      mathConcept: "Fundamental Thm of Arithmetic",
      mathConceptDescription: "Gamifies the Unique Factorization Theorem. Every integer greater than 1 is either a prime or a product of primes. Players intuitively learn to decompose numbers into their prime factors to defeat them.",
      featured: true,
    },
    {
      id: "sieve-speedrun",
      name: "The Sieve Speedrun",
      description: "Race against the clock! Eliminate multiples to discover primes using the Sieve of Eratosthenes.",
      difficulty: "Medium",
      players: "1 Player",
      duration: "1-3 min",
      tags: ["Math", "Speed", "Algorithm"],
      mathConcept: "Sieve of Eratosthenes",
      mathConceptDescription: "Gamifies the ancient algorithm for finding prime numbers. By systematically eliminating multiples of 2, 3, 5, and 7, players visually grasp why the remaining numbers MUST be prime.",
    },
  ],
  "Strategy Games": [
    {
      id: "15-puzzle",
      name: "15 Puzzle Race",
      description: "Race to arrange the numbers in order! Competitive sliding tile puzzle with split-screen.",
      difficulty: "Hard",
      players: "1-2 Players",
      duration: "10-20 min",
      tags: ["Logic", "Speed", "Puzzle"],
      mathConcept: "Permutation Groups & Invariants",
      mathConceptDescription: "A classic problem in Group Theory. The puzzle demonstrates the concept of 'parity' (odd vs even permutations). Not all arrangements are reachable because the parity of the puzzle is invariant under valid moves.",
    },
    {
      id: "brick-wall",
      name: "Brick Wall Game",
      description:
        "Strategic wall demolition - remove 1-2 adjacent bricks per turn, last player wins!",
      difficulty: "Medium",
      players: "2 Players",
      duration: "5-12 min",
      tags: ["Strategy", "Logic", "Turn-based", "Nim-variant"],
      mathConcept: "Modular Arithmetic & Nim",
      mathConceptDescription: "Mathematically equivalent (isomorphic) to the game of Nim. The winning strategy often involves calculating remainders (modulo arithmetic) to maintain a specific invariant that forces the opponent into a losing position.",
    },
    {
      id: "coin-flip",
      name: "Coin Flip Challenge",
      description:
        "A strategic two-player game where you flip coins to outmaneuver your opponent",
      difficulty: "Medium",
      players: "2 Players",
      duration: "5-15 min",
      tags: ["Logic", "Strategy", "Turn-based"],
      mathConcept: "Probability & Game Theory",
      mathConceptDescription: "Explores decision-making under uncertainty and zero-sum game dynamics. Players learn to anticipate opponent moves and calculate optimal strategies based on limited options.",
      featured: true,
    },
    {
      id: "connect4",
      name: "Connect 4",
      description: "Vertical strategy! Drop discs to connect 4 in a row before your opponent.",
      difficulty: "Medium",
      players: "2 Players",
      duration: "5-10 min",
      tags: ["Strategy", "Pattern", "Classic"],
      mathConcept: "Spatial Reasoning & Heuristics",
      mathConceptDescription: "A solved game that introduces heuristic search. Players must scan for patterns (lines of 3, traps) and look ahead, making it a perfect introduction to algorithmic thinking.",
    },
    {
      id: "chomp",
      name: "Game of Chomp",
      description:
        "Strategic cookie chomping game - avoid the poison cookie in the corner!",
      difficulty: "Medium",
      players: "2 Players",
      duration: "3-10 min",
      tags: ["Strategy", "Logic", "Turn-based"],
      mathConcept: "Combinatorial Game Theory",
      mathConceptDescription: "A classic example of an 'impartial game'. It introduces the concept of 'terminal states' and 'winning positions'. Ideally played by working backwards from the loss condition to find the guaranteed winning move.",
    },
    {
      id: "mancala",
      name: "Mancala",
      description: "The ancient game of sowing seeds. Capture opponents' seeds in this strategic counting game.",
      difficulty: "Medium",
      players: "2 Players",
      duration: "10-15 min",
      tags: ["Strategy", "Counting", "Classic"],
      mathConcept: "Counting & Modulo Arithmetic",
      mathConceptDescription: "Involves precise counting and distribution (sowing). Players intuitively perform modulo arithmetic to predict where their last seed will land, aiming to capture or take another turn.",
    },
    {
      id: "nim",
      name: "Nim",
      description:
        "The classic game of strategy. Force your opponent to take the last object!",
      difficulty: "Medium",
      players: "2 Players",
      duration: "5-10 min",
      tags: ["Strategy", "Logic", "Classic"],
      mathConcept: "Binary Numbers & XOR Sums",
      mathConceptDescription: "The foundation of impartial game theory. The winning strategy relies on the 'Nim-sum', which is calculated using the XOR operation on the binary representation of the heap sizes.",
    },
    {
      id: "hanoi",
      name: "Tower of Hanoi",
      description: "Mathematical puzzle. Move the stack of disks to the last rod, following the rules of recursion.",
      difficulty: "Variable",
      players: "1 Player",
      duration: "2-10 min",
      tags: ["Logic", "Recursion", "Classic"],
      mathConcept: "Recursion & Exp. Growth",
      mathConceptDescription: "The quintessential example of recursion. The minimum moves required is 2^n - 1, demonstrating exponential growth. The solution involves solving a smaller version of the same problem (moving n-1 disks).",
    },
    {
      id: "ultimate-tic-tac-toe",
      name: "Ultimate Tic-Tac-Toe",
      description: "A deep strategy game played on 9 boards at once. Win 3 local boards in a row to win the global game!",
      difficulty: "Hard",
      players: "2 Players",
      duration: "10-20 min",
      tags: ["Strategy", "Logic", "Meta-Game"],
      mathConcept: "Fractal Structure & Strategy",
      mathConceptDescription: "Demonstrates recursive systems where a small state (local board) influences the large state (global board). It requires thinking on multiple levels of abstraction simultaneously and managing resources (moves) to maximize global advantage.",
      featured: true,
    },
    {
      id: "chinese-checkers",
      name: "Chinese Checkers",
      description: "Classic marble race! Strategy and long jumps to the opposite side.",
      difficulty: "Medium",
      players: "1-6 Players",
      duration: "10-30 min",
      tags: ["Strategy", "Multiplayer", "Classic"],
      mathConcept: "Network Topology & Shortest Path",
      mathConceptDescription: "An optimization problem on a graph. Players must find the shortest path through a dynamic network, using other pieces as 'bridges' to maximize displacement per turn.",
    },
    {
      id: "reversi",
      name: "Reversi",
      description: "Classic strategy board game. Outflank your opponent to flip their pieces to your color.",
      difficulty: "Medium",
      players: "1 Player (vs AI)",
      duration: "5-10 min",
      tags: ["Strategy", "Logic", "Classic"],
      mathConcept: "Algorithmic Strategy & Minimax",
      mathConceptDescription: "Introduces zero-sum game theory and heuristic search. Players must maximize their territory while minimizing the opponent's options.",
    },
  ],
  "Set Theory & Foundations": [
    {
      id: "pattern-match",
      name: "Pattern Match",
      description: "Fast-paced visual perception game. Find sets of 3 cards where each feature is either all the same or all different.",
      difficulty: "Medium",
      players: "1 Player",
      duration: "5-20 min",
      tags: ["Logic", "Pattern", "Set Theory"],
      mathConcept: "Combinatorics & Finite Geometry",
      mathConceptDescription: "Isomorphic to finding lines in a finite affine geometry vector space AG(4, 3). Each card is a point, and a valid set is a line.",
      featured: true,
    },
    {
      id: "venn-blitz",
      name: "Venn Blitz",
      description: "Fast-paced set sorting! Categorize numbers into the correct region of the Venn Diagram.",
      difficulty: "Medium",
      players: "1 Player",
      duration: "1 min",
      tags: ["Logic", "Sets", "Speed"],
      mathConcept: "Set Theory",
      mathConceptDescription: "Gamifies the concepts of Union (A ∪ B), Intersection (A ∩ B), and Complements. Players must rapidly evaluate logical propositions to sort elements correctly.",
    },
  ],
};

// Main Game List Component
const GameList = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const totalGames = Object.values(gameCategories).flat().length;
  const availableGames = Object.values(gameCategories)
    .flat()
    .filter((game) => !game.comingSoon).length;

  const filteredCategories = Object.entries(gameCategories).reduce((acc, [category, games]) => {
    const filteredGames = games.filter(game => 
      game.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filteredGames.length > 0) {
      acc[category] = filteredGames;
    }
    return acc;
  }, {});

  const hasResults = Object.keys(filteredCategories).length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <>
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Master Math Through Play
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Challenge yourself with strategic games, quick calculations, and
              brain-teasing puzzles. Learn mathematics while having fun!
            </p>

            <div className="flex justify-center space-x-8 text-center mb-5">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold text-blue-600">
                  {totalGames}
                </div>
                <div className="text-sm text-gray-600">Total Games</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold text-green-600">
                  {availableGames}
                </div>
                <div className="text-sm text-gray-600">Available Now</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold text-purple-600">∞</div>
                <div className="text-sm text-gray-600">Learning Fun</div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="max-w-xl mx-auto relative mb-12">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-full leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm shadow-sm transition-shadow hover:shadow-md"
                    placeholder="Search games..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search games"
                />
            </div>
          </div>

          {/* Smart Wallet Picks Banner */}
          <AdBanner />

          {/* Game Categories */}
          {hasResults ? (
              Object.entries(filteredCategories).map(([category, games]) => (
                <section key={category} className="mb-12">
                  <div className="flex items-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">{category}</h3>
                    <ChevronRight className="h-5 w-5 text-gray-400 ml-2" />
                    <span className="text-sm text-gray-500 ml-2">
                      {games.length} games
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {games.map((game) => (
                      <GameCard key={game.id} game={game} />
                    ))}
                  </div>
                </section>
              ))
          ) : (
              <div className="text-center py-12">
                  <p className="text-xl text-gray-500">No games found matching "{searchQuery}".</p>
                  <button 
                    onClick={() => setSearchQuery("")}
                    className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
                  >
                      Clear Search
                  </button>
              </div>
          )}
        </>
      </main>
    </div>
  );
};

export default GameList;
